// The values written over Steps 2 and 3 with ?edit=1, and the one place they are applied to a
// building, so both steps read the same overridden facts and everything worked out from them
// follows (docs/Y2026-Q4/design-amazon-overview-overrides.md). Keys must agree with
// src/inrange_agents/amazon_building_notes.py.
import type { BuildingNote } from "../../api/hooks/useAmazonBuildingNotes";
import type {
  AmazonBuilding,
  AmazonNeed,
  BuildingStep,
  StepState,
} from "../../api/hooks/useAmazonOverview";
import { day, formatDate } from "./dates";
import { blocksItsStep, countedInputs, INPUT_STEPS } from "./needs";

export type Kind = "state" | "need" | "date" | "number" | "flag" | "text";

export const STEP_FIELDS = {
  state: "state",
  date: "date",
  due: "date",
  estimated: "flag",
  hidden: "flag",
  kwp: "number",
  kw_ac: "number",
  cell: "text",
  line: "text",
  source: "text",
  sent_by: "text",
  waiting_since: "date",
  delivers: "text",
  finding: "text",
  note: "text",
} as const;
export type StepField = keyof typeof STEP_FIELDS;

export const BUILDING_FIELDS = {
  site_code: "text",
  verdict: "text",
  achievable_caption: "text",
  committed_cod: "date",
  achievable_cod: "date",
  committed_kwp: "number",
  committed_kw_ac: "number",
  achievable_kwp: "number",
  achievable_kw_ac: "number",
  cod_reason: "text",
} as const;
export type BuildingField = keyof typeof BUILDING_FIELDS;

export type ContractField = "design" | "llsignoff" | "install";

/** A step's card line has the bare step id as its key, as the notes always have. */
export const cardKey = (step: string): string => step;
export const stepKey = (step: string, field: StepField): string => `${step}.${field}`;
export const buildingKey = (field: BuildingField): string => `building.${field}`;
export const contractKey = (field: ContractField): string => `contract.${field}`;
export const needKey = (need: string, field: "status" | "date"): string =>
  `need.${need}.${field}`;
/** Whether one of a blocker's open asks is hidden from readers. */
export const askKey = (actionId: string): string => `ask.${actionId}.hidden`;

const FACT_FIELDS: StepField[] = ["state", "date", "due", "estimated", "kwp", "kw_ac"];

/** The keys behind a step's facts, which every view of the step reads. */
export const stepFactKeys = (step: string): string[] =>
  FACT_FIELDS.map((f) => stepKey(step, f));
/** What a Step 2 cell and its hover card say beyond the step's dates and sizes. */
export const CARD_FIELDS = ["source", "sent_by", "waiting_since", "delivers", "finding", "note"] as const;

/** What a Step 2 cell shows: the facts, its wording, and its hover card's record. */
export const cellKeys = (step: string): string[] => [
  ...stepFactKeys(step),
  stepKey(step, "cell"),
  stepKey(step, "line"),
  ...CARD_FIELDS.map((f) => stepKey(step, f)),
];
/** What a Step 3 card shows: the facts, its line and whether it is hidden. */
export const cardKeys = (step: string): string[] => [
  ...stepFactKeys(step),
  cardKey(step),
  stepKey(step, "hidden"),
];

export type Overrides = ReadonlyMap<string, BuildingNote>;
export const NO_OVERRIDES: Overrides = new Map();

export const overridesByBuilding = (
  notes: BuildingNote[],
): Map<string, Map<string, BuildingNote>> => {
  const out = new Map<string, Map<string, BuildingNote>>();
  for (const n of notes) {
    const own = out.get(n.project_id) ?? new Map<string, BuildingNote>();
    own.set(n.step_id, n);
    out.set(n.project_id, own);
  }
  return out;
};

const number = (v: string): number => Number(v);

const applyStep = (id: string, s: BuildingStep, o: Overrides): BuildingStep => {
  const v = (f: StepField) => o.get(stepKey(id, f))?.text;
  const state = v("state");
  const date = v("date");
  const due = v("due");
  const estimated = v("estimated");
  const kwp = v("kwp");
  const kwAc = v("kw_ac");
  // Design inputs' cell is its count, which its hover card's status and its Step 3 card
  // repeat: words written over the cell are the count wherever it shows.
  const detail = id === "inputs" ? v("cell") : undefined;
  const card = Object.fromEntries(
    CARD_FIELDS.flatMap((f) => (v(f) === undefined ? [] : [[f, v(f)]])),
  ) as Partial<Pick<BuildingStep, (typeof CARD_FIELDS)[number]>>;
  if (
    [state, date, due, estimated, kwp, kwAc, detail].every((x) => x === undefined) &&
    !Object.keys(card).length
  )
    return s;
  return {
    ...s,
    ...card,
    state: (state as StepState | undefined) ?? s.state,
    date: date ?? s.date,
    due: due ?? s.due,
    estimated: estimated === undefined ? s.estimated : estimated === "true",
    kwp: kwp === undefined ? s.kwp : number(kwp),
    kw_ac: kwAc === undefined ? s.kw_ac : number(kwAc),
    detail: detail ?? s.detail,
  };
};

const applyNeed = (n: AmazonNeed, o: Overrides): AmazonNeed => {
  const status = o.get(needKey(n.key, "status"))?.text;
  const date = o.get(needKey(n.key, "date"))?.text;
  if (status === undefined && date === undefined) return n;
  // An override written as "not_asked" before 19 Oct 2026 reads not received, as the
  // backend reads an item nobody has asked for.
  const written = (status === "not_asked" ? "missing" : status) as AmazonNeed["status"] | undefined;
  return {
    ...n,
    status: written ?? n.status,
    date: date ?? n.date,
  };
};

/** A step as the items it needs leave it, the rule amazon_overview._blocked_by_items
 * applies: blocked while any of them holds it up, else as its own record has it, and never
 * once done or not needed here. An API deployed before ``own_state`` leaves it as sent. */
const withItems = (id: string, s: BuildingStep, needs: AmazonNeed[]): BuildingStep => {
  const own = s.own_state;
  if (!own) return s;
  const held =
    own === "done" || own === "not_applicable"
      ? 0
      : needs.filter((n) => n.step === id && blocksItsStep(n)).length;
  return held
    ? { ...s, state: "blocked", blocking_items: held }
    : { ...s, state: own, blocking_items: 0 };
};

/** Amazon design inputs as the backend reads it from the needs it counts: open while any
 * is still to come, never blocked by them (they block the steps that need them). */
const inputsFromNeeds = (
  step: BuildingStep,
  needs: AmazonNeed[],
  settled: Set<string>,
): BuildingStep => {
  const counted = countedInputs(needs, (s) => settled.has(s));
  const count = (status: AmazonNeed["status"]) =>
    counted.filter((n) => n.status === status).length;
  if (!counted.length)
    return INPUT_STEPS.every((x) => settled.has(x))
      ? { ...step, state: "done", date: "", detail: "" }
      : { ...step, state: "not_tracked", detail: "" };
  const wanted = counted.length - count("not_needed");
  if (count("received") === wanted) {
    const dates = counted.filter((n) => n.status === "received").map((n) => n.date);
    return { ...step, state: "done", date: dates.sort()[dates.length - 1] ?? "", detail: "" };
  }
  const detail = `${count("received")} received, ${wanted - count("received")} not received`;
  return { ...step, state: "open", detail, blocking_items: 0 };
};

/** ``b`` as the page shows it: every fact written over it applied. Wording is applied where
 * it is rendered, since the page writes its own text from these facts. */
export const applyOverrides = (b: AmazonBuilding, o: Overrides): AmazonBuilding => {
  if (!o.size) return b;
  const needs = b.needs?.map((x) => applyNeed(x, o));
  const inputsMoved =
    INPUT_STEPS.some((x) => o.has(stepKey(x, "state"))) ||
    (b.needs ?? []).some(
      (x) => x.design_input && (o.has(needKey(x.key, "status")) || o.has(needKey(x.key, "date"))),
    );
  const v = (f: BuildingField) => o.get(buildingKey(f))?.text;
  const n = (f: BuildingField, own: number | null) => {
    const x = v(f);
    return x === undefined ? own : number(x);
  };
  const c = (f: ContractField) => o.get(contractKey(f))?.text;
  const dates = b.contract_dates ?? { design: "", llsignoff: "", install: "" };
  // Design inputs follows the overridden design state as well as the needs; its
  // own override still has the last word.
  const withInputs = (steps: Record<string, BuildingStep>): Record<string, BuildingStep> => {
    if (!inputsMoved || !needs || !b.steps.inputs) return steps;
    const settled = new Set(
      INPUT_STEPS.filter((x) => ["done", "not_applicable"].includes(steps[x]?.state ?? "")),
    );
    return {
      ...steps,
      inputs: applyStep("inputs", inputsFromNeeds(b.steps.inputs, needs, settled), o),
    };
  };
  const reason = v("cod_reason");
  const basis = b.achievable_cod_basis ?? null;
  return {
    ...b,
    achievable_cod_basis:
      reason === undefined
        ? b.achievable_cod_basis
        : { by: basis?.by ?? "", set_at: basis?.set_at ?? "", accounts_for: basis?.accounts_for ?? "", reason },
    site_code: v("site_code") ?? b.site_code,
    committed_cod: v("committed_cod") ?? b.committed_cod,
    achievable_cod: v("achievable_cod") ?? b.achievable_cod,
    committed_kwp: n("committed_kwp", b.committed_kwp),
    committed_kw_ac: n("committed_kw_ac", b.committed_kw_ac),
    achievable_kwp: n("achievable_kwp", b.achievable_kwp),
    achievable_kw_ac: n("achievable_kw_ac", b.achievable_kw_ac),
    achievable_written: {
      kwp: o.has(buildingKey("achievable_kwp")),
      kwAc: o.has(buildingKey("achievable_kw_ac")),
    },
    // Each step as the overridden items leave it, then its own override, which has the
    // last word.
    steps: withInputs(
      Object.fromEntries(
        Object.entries(b.steps).map(([id, s]) => [
          id,
          applyStep(id, needs ? withItems(id, s, needs) : s, o),
        ]),
      ),
    ),
    needs,
    contract_dates: {
      design: c("design") ?? dates.design,
      llsignoff: c("llsignoff") ?? dates.llsignoff,
      install: c("install") ?? dates.install,
    },
  };
};

const order = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** Earliest committed COD first, none last, then name, compared as the backend's sort
 * compares them (by code point), so with no override the order is the API's. */
export const byCommittedCod = (a: AmazonBuilding, b: AmazonBuilding): number =>
  Number(!a.committed_cod) - Number(!b.committed_cod) ||
  order(a.committed_cod, b.committed_cod) ||
  order(a.name, b.name);

export const wordingOf = (o: Overrides, key: string): string | undefined =>
  o.get(key)?.text;

/** A blocker card or an open ask written over as hidden from readers. */
export const isHidden = (o: Overrides, key: string): boolean => o.get(key)?.text === "true";

/** What the store keeps of a platform value (amazon_building_notes.TEXT_MAX). */
const PLATFORM_MAX = 300;

const numberText = (x: number | null | undefined): string => (x == null ? "" : String(x));

/** What the platform holds for a fact key, as a value would be written; undefined for a
 * wording key, whose platform value is the text the page writes. */
export const platformValue = (b: AmazonBuilding, key: string): string | undefined => {
  const [head, a, f] = key.split(".");
  if (head === "building") {
    const field = a as BuildingField;
    if (field === "site_code") return b.site_code;
    if (field === "cod_reason") return b.achievable_cod_basis?.reason ?? "";
    if (BUILDING_FIELDS[field] === "text") return undefined;
    const raw =
      b[field as Exclude<BuildingField, "verdict" | "achievable_caption" | "site_code" | "cod_reason">];
    return typeof raw === "number" || raw === null ? numberText(raw) : raw;
  }
  if (head === "contract") return b.contract_dates?.[a as ContractField] ?? "";
  if (head === "ask") return "false";
  if (head === "need") {
    const need = b.needs?.find((x) => x.key === a);
    return need ? (f === "status" ? need.status : need.date) : "";
  }
  const step = b.steps[head];
  if (a === undefined || !step) return undefined;
  switch (a as StepField) {
    case "state":
      return step.state;
    case "date":
      return step.date;
    case "due":
      return step.due;
    case "estimated":
      return String(step.estimated);
    case "hidden":
      return "false";
    case "kwp":
      return numberText(step.kwp);
    case "kw_ac":
      return numberText(step.kw_ac);
    case "source":
    case "sent_by":
    case "waiting_since":
    case "delivers":
    case "finding":
    case "note":
      return step[a as (typeof CARD_FIELDS)[number]] ?? "";
    default:
      return undefined;
  }
};

/** The platform has moved since the note was written: the page showed something else then.
 * Only facts say so: wording written by the page counts days from today, so its text moves
 * every day without the platform moving, and the pane shows it beside an override instead. */
export const drifted = (note: BuildingNote | undefined, platform: string | undefined): boolean =>
  note?.platform != null &&
  platform !== undefined &&
  note.platform !== Array.from(platform).slice(0, PLATFORM_MAX).join("");

export const kindOf = (key: string): Kind => {
  const [head, a, f] = key.split(".");
  if (head === "building") return BUILDING_FIELDS[a as BuildingField];
  if (head === "contract") return "date";
  if (head === "ask") return "flag";
  if (head === "need") return f === "status" ? "need" : "date";
  return a === undefined ? "text" : STEP_FIELDS[a as StepField];
};

export const STATE_OPTIONS: [StepState, string][] = [
  ["done", "Done"],
  ["open", "In progress"],
  ["blocked", "Blocked"],
  ["not_started", "Not started"],
  ["not_tracked", "Not happened yet"],
  ["not_applicable", "Not applicable"],
];

/** Partially received only means something on a graded item (``AmazonNeed.graded``), the
 * only kind the edit pane offers it on. */
export const NEED_OPTIONS: [AmazonNeed["status"], string][] = [
  ["received", "Received"],
  ["partial", "Partially received"],
  ["missing", "Not received"],
  ["not_needed", "Not applicable"],
];

/** A value in words, as the pane shows it. */
export const shownValue = (kind: Kind, v: string): string => {
  if (kind === "flag") return v === "true" ? "Yes" : "No";
  if (!v) return kind === "text" ? "Nothing" : "Not recorded";
  if (kind === "date") return formatDate(day(v));
  if (kind === "number") return Number(v).toLocaleString("en-GB");
  if (kind === "state") return STATE_OPTIONS.find(([s]) => s === v)?.[1] ?? v;
  // An item nobody has chased is as not received as one that was.
  if (kind === "need")
    return v === "not_asked" ? "Not received" : (NEED_OPTIONS.find(([s]) => s === v)?.[1] ?? v);
  return v;
};
